import React, { useMemo } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { meanBy, sumBy } from "lodash";

import { Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import { GetMultiProjectAreasStatisticsResponse } from "fond/api/multiProjectsSlice";
import { getImportantFiberProviders } from "fond/cityPlanner/helper";
import { Maybe, MultiProject } from "fond/types";
import { Report } from "fond/types/report";
import { formatCurrency } from "fond/utils/currency";
import { formatFractionPercent } from "fond/utils/number";

interface IProps {
  filteredNodes: Report[];
  multiProject: Maybe<MultiProject>;
  statistics: GetMultiProjectAreasStatisticsResponse;
}

const CityReportStatistics: React.FC<IProps> = ({ filteredNodes, multiProject, statistics }: IProps) => {
  const filteredStatistics = useMemo(() => {
    return statistics.Areas.filter((area) => filteredNodes.some((node) => node.MultiProjectArea?.ID === area.Area.ID));
  }, [filteredNodes, statistics]);

  const importantProviders = useMemo(() => new Set(getImportantFiberProviders(multiProject)), [multiProject]);

  const {
    sduProportionLocal: sduProportion,
    weightedMedianIncomeLocal: weightedMedianIncome,
    underservedProportionLocal: underservedProportion,
    providerCoverageLocal: providerCoverage,
  } = useMemo(() => {
    const weightedMedianIncomeLocal =
      filteredStatistics.length > 0 ? sumBy(filteredStatistics, "WeightedMedianIncome.WeightedMedianIncomeDollars") / filteredStatistics.length : 0;
    const sduProportionLocal = meanBy(filteredStatistics, "AddressBreakdown.Sdu.Proportion");
    const underservedProportionLocal = meanBy(filteredStatistics, "AddressUnderservedProportion.AddressUnderservedProportion");

    // Combine the provider fiber coverage proportions, weighted by the address count in each area.
    // e.g. if area 1 has 500 addresses and 20% coverage, and area 2 has 10 addresses and 90% coverage,
    //      then the overall fiber coverage is (0.2*500 + 0.9*10) / (500 + 10) ~= 21%
    const providerAddressCounts: Record<string, number> = {};
    filteredStatistics.forEach((areaStatistics) => {
      Object.entries(areaStatistics.ProviderFiberCoverage).forEach(([provider, coverage]) => {
        if (!importantProviders.has(provider)) return;
        providerAddressCounts[provider] =
          (providerAddressCounts[provider] || 0) + coverage.FiberCoverageProportion * areaStatistics.AddressCounts.AddressCount;
      });
    });
    const totalAddressCount = sumBy(filteredStatistics, "AddressCounts.AddressCount");
    const providerCoverageLocal = Object.entries(providerAddressCounts).map(([provider, addressCount]) => ({
      provider,
      proportion: totalAddressCount > 0 ? addressCount / totalAddressCount : 0,
    }));

    return {
      sduProportionLocal,
      weightedMedianIncomeLocal,
      underservedProportionLocal,
      providerCoverageLocal,
    };
  }, [filteredStatistics, importantProviders]);

  const statisticRows = [
    { title: "Median Income", value: formatCurrency(weightedMedianIncome) },
    { title: "Underserved proportion", value: formatFractionPercent(underservedProportion) },
    { title: "SDU Proportion", value: formatFractionPercent(sduProportion) },
    ...providerCoverage.map(({ provider, proportion }) => ({ title: `${provider} Coverage`, value: formatFractionPercent(proportion) })),
  ];

  return (
    <Grid item xs={12} sx={{ height: 320 }} data-testid="city-report-statistics">
      <Paper
        variant="elevation"
        elevation={1}
        square={false}
        sx={{ display: "flex", flexDirection: "column", height: "100%", p: 3, overflow: "hidden" }}
      >
        <Typography variant="h6">Statistics</Typography>
        <Box sx={{ mt: 1 }}>
          <Table maxHeight={225} variant="ag-grid-compact-scrollable">
            <TableHead>
              <TableRow>
                <TableCell>Criteria</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statisticRows.map((row) => (
                <TableRow key={row.title}>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Grid>
  );
};
export default CityReportStatistics;
